import type {
  ThemeOptions,
  ComponentsProps,
  ComponentsOverrides,
  ComponentsVariants
} from '@mui/material/styles';

import type { Theme } from '@ui/ThemeRegistry/theme.types';

const defaultProps: ComponentsProps['Blog'] = {};

const styleOverrides: ComponentsOverrides<Theme>['Blog'] = {
  root: ({ theme, ownerState }) => ({
    'containerType': 'inline-size',
    'position': 'relative',
    'width': '100%',
    'display': 'flex',
    'flexDirection': 'column',

    '& > *:not([class*="Hero-root"])': {
      paddingTop: 'var(--section-padding)',
      paddingBottom: 'var(--section-padding)'
    }
  }),

  contentOuterGrid: () => ({
    paddingTop: 'calc(var(--section-padding) / 2) !important',
    paddingBottom: 'calc(var(--section-padding) / 2) !important'
  }),

  headerWrap: {
    gridColumn: 'start/end',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--grid-gap)'
  },

  title: {
    'fontSize': 'var(--h5-font-size)',
    'lineHeight': 'var(--h5-line-height)',
    'fontWeight': 'var(--h5-font-weight)',
    'margin': 'var(--h5-margin)',

    '@container (min-width: 768px)': {
      fontSize: 'var(--h3-font-size)',
      lineHeight: 'var(--h3-line-height)',
      fontWeight: 'var(--h3-font-weight)',
      margin: 'var(--h3-margin)'
    },

    '@container (min-width: 1024px)': {
      fontSize: 'var(--h2-font-size)',
      lineHeight: 'var(--h2-line-height)',
      fontWeight: 'var(--h2-font-weight)',
      margin: 'var(--h2-margin)'
    }
  },

  featuredMediaWrap: ({ theme }) => ({
    'display': 'flex',
    'justifyContent': 'center',
    'gridColumn': 'start/end',

    [theme.containerBreakpoints.up('lg')]: {
      gridColumn: 'two-start/eleven-end !important'
    },

    '& :is(video, img, svg, picture)': {
      borderRadius: 'var(--grid-gap-double)',
      paddingBottom: 'var(--grid-gap)'
    }
  }),

  shareLinksWrap: {
    marginTop: 'var(--grid-gap-double)',
    marginBottom: 'var(--grid-gap-double)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--grid-gap)'
  },

  shareLinksLabel: {
    fontSize: 'var(--body1-font-size)',
    lineHeight: 'var(--body1-line-height)',
    fontWeight: 'var(--body1-font-weight)',
    margin: 'var(--body1-margin)'
  },

  shareLinks: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--grid-gap)'
  },

  shareLink: ({ theme }) => ({
    'display': 'flex',
    'alignItems': 'center',
    'gap': 'var(--grid-gap-quarter)',
    'padding': 'var(--grid-gap-half)',
    'borderRadius': 'var(--grid-gap-quarter)',
    'transition': theme.transitions.create(['background-color']),
    'color': theme.palette.text.primary,

    '&:hover': {
      backgroundColor: theme.palette.action.hover
    },

    '& svg': {
      width: '1.5em',
      height: '1.5em'
    }
  }),

  bodyHeader: {
    '&:not(:first-of-type)': {
      paddingTop: 'var(--grid-gap)',
      marginTop: 'var(--grid-gap)',
      borderTop: 'solid',
      borderTopWidth: '1px'
    }
  },

  contentWrap: ({ theme }) => ({
    gridColumn: 'start/end',

    [theme.containerBreakpoints.up('lg')]: {
      gridColumn: 'two-start/eleven-end !important'
    },

    img: {
      padding: 'var(--grid-gap) 0',

      [theme.containerBreakpoints.up('md')]: {
        padding: 'var(--section-padding) 0',
        maxWidth: '75%',
        margin: '0 auto'
      }
    }
  }),

  body: () => ({}),

  breadcrumbsWrap: {
    gridColumn: 'start/end',
    marginBottom: 'var(--grid-gap)'
  },

  blogCategories: {
    display: 'flex',
    gap: 'var(--grid-gap)',
    flexWrap: 'wrap'
  },

  blogCategory: {
    fontSize: 'var(--bodySmall-font-size)',
    lineHeight: 'var(--bodySmall-line-height)',
    fontWeight: 'var(--bodySmall-font-weight)',
    margin: 'var(--bodySmall-margin)'
  },

  tags: {
    display: 'flex',
    gap: 'var(--grid-gap)',
    flexWrap: 'wrap'
  },

  tag: {
    fontSize: 'var(--bodySmall-font-size)',
    lineHeight: 'var(--bodySmall-line-height)',
    fontWeight: 'var(--bodySmall-font-weight)',
    margin: 'var(--bodySmall-margin)'
  },

  relatedItemsWrap: {
    gridColumn: 'start/end',
    marginTop: 'var(--grid-gap-double)'
  },

  authorDetailsWrap: {
    marginTop: 'var(--grid-gap-double)',
    padding: 'var(--grid-gap-double)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--grid-gap)',
    backgroundColor: 'var(--mui-palette-background-paper)',
    borderRadius: 'var(--grid-gap)'
  },

  authorName: {
    fontSize: 'var(--h5-font-size)',
    lineHeight: 'var(--h5-line-height)',
    fontWeight: 'var(--h5-font-weight)',
    margin: 'var(--h5-margin)'
  },

  authorSummaryWrap: {
    fontSize: 'var(--body1-font-size)',
    lineHeight: 'var(--body1-line-height)',
    fontWeight: 'var(--body1-font-weight)',
    margin: 'var(--body1-margin)'
  },

  authorSocialLinks: {
    display: 'flex',
    gap: 'var(--grid-gap)',
    flexWrap: 'wrap'
  },

  sideContentWrap: {
    gridColumn: 'start/end',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--grid-gap-double)'
  },

  sideContentInnerWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--grid-gap)'
  }
};

const createVariants = (_theme: Theme): ComponentsVariants['Blog'] => [];

export const blogTheme = (theme: Theme): ThemeOptions => ({
  components: {
    Blog: {
      defaultProps,
      styleOverrides,
      variants: createVariants(theme)
    }
  }
});

export default blogTheme;
